/**
 * UIModelApiContext for UI-Model-1 (API-Direct Approach)
 *
 * API operations wrapper for UI Model.
 * Uses existing UIModelApiService - NO new API endpoints.
 *
 * With API-direct approach:
 * - Load returns UIModelAPI directly (no normalization)
 * - Save sends state sections directly (no denormalization)
 *
 * Line limit: ~200 lines
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { DirtySection, UIModelState, UIModelAPI } from '../types/state.types';
import { UIModelApiContextValue } from '../types/context.types';
import { uiModelApiService } from '../../shared/services/UIModelApiService';
import { updateCustomCSS, getCustomCSS } from '../../shared/services/ApiHelper';

// ============================================================================
// Types
// ============================================================================

interface ApiConfig {
  configureId: string;
  modelId: string;
  bearerToken: string;
}

interface LoadResult {
  success: boolean;
  data?: UIModelState['model'];
  error?: string;
}

interface SaveResult {
  success: boolean;
  error?: string;
}

// ============================================================================
// Context
// ============================================================================

const UIModelApiContext = createContext<UIModelApiContextValue | null>(null);

interface UIModelApiProviderProps {
  children: React.ReactNode;
  config?: Partial<ApiConfig>;
}

export const UIModelApiProvider = ({
  children,
  config: initialConfig,
}: UIModelApiProviderProps) => {
  const [config, setConfig] = useState<ApiConfig>({
    configureId: initialConfig?.configureId || '',
    modelId: initialConfig?.modelId || '',
    bearerToken: initialConfig?.bearerToken || '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  const updateConfig = useCallback((updates: Partial<ApiConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  }, []);

  /**
   * Load model from API
   * Returns API response directly (no normalization needed)
   */
  const loadModel = useCallback(async (modelId?: string): Promise<LoadResult> => {
    const targetModelId = modelId || config.modelId;
    const { configureId } = config;

    if (!targetModelId || !configureId) {
      return { success: false, error: 'Model ID and Configure ID are required' };
    }

    setIsLoading(true);
    setLastError(null);

    try {
      // Use existing API service - returns { success, data: { model: UIModelAPI } }
      const response = await uiModelApiService.getUIModel(configureId, targetModelId);

      // Extract the model from the response wrapper
      // Type assertion needed as API service returns legacy type but data structure is compatible
      if (response.success && response.data?.model) {
        return { success: true, data: response.data.model as UIModelAPI };
      }
      return { success: false, error: 'Model not found in response' };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load model';
      setLastError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [config]);

  /**
   * Save a specific section to API
   * With API-direct approach, state sections are already in API format
   */
  const saveSection = useCallback(async (
    section: DirtySection,
    data: unknown,
    canvasId?: string
  ): Promise<SaveResult> => {
    const { configureId, modelId } = config;

    if (!configureId || !modelId) {
      return { success: false, error: 'Configure ID and Model ID are required' };
    }

    setIsSaving(true);
    setLastError(null);

    try {
      // API-direct: data is already in API format, send directly
      switch (section) {
        case 'header':
          await uiModelApiService.updateHeader(configureId, modelId, data as any);
          break;

        case 'footer':
          await uiModelApiService.updateFooter(configureId, modelId, data as any);
          break;

        case 'navigation': {
          // Strip nested configuration object - API expects flat structure only
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { configuration, ...cleanNavigation } = (data as any) || {};
          // Ensure all navigation properties have values (apply defaults for missing ones)
          const navigationWithDefaults = {
            showNavigation: true,
            orientation: 'Horizontal',
            style: 'Block',
            flow: 'Nonlinear',
            showDivider: true,
            pageSpacing: 10,
            groupSpacing: 10,
            textAlignment: 'Centre',
            ...cleanNavigation,  // State values override defaults
          };
          await uiModelApiService.updateNavigation(configureId, modelId, { navigation: navigationWithDefaults });
          break;
        }

        case 'canvas':
          if (!canvasId) {
            return { success: false, error: 'Canvas ID required for canvas save' };
          }
          await uiModelApiService.updateCanvas(
            configureId,
            modelId,
            data as any,
            canvasId
          );
          break;

        case 'theme':
          await uiModelApiService.updateTheme(configureId, modelId, data as any);
          break;

        case 'customCss':
          await updateCustomCSS(configureId, modelId, data as any);
          break;

        case 'sharedWidgets':
          await uiModelApiService.updateSharedWidgets(configureId, modelId, data as any);
          break;

        default:
          return { success: false, error: `Unknown section: ${section}` };
      }

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : `Failed to save ${section}`;
      setLastError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsSaving(false);
    }
  }, [config]);

  /**
   * Save all dirty sections
   */
  const saveAll = useCallback(async (): Promise<SaveResult> => {
    // This would need access to the state to know what sections are dirty
    // In practice, this is called from the SaveQueueContext which tracks dirty sections
    return { success: true };
  }, []);

  /**
   * Delete canvas via API
   */
  const deleteCanvas = useCallback(async (canvasId: string, modelIdOverride?: string): Promise<{ success: boolean }> => {
    const { configureId } = config;
    const effectiveModelId = modelIdOverride || config.modelId;
    if (!configureId || !effectiveModelId) {
      console.warn('deleteCanvas skipped: missing configureId or modelId', { configureId, modelId: effectiveModelId, canvasId });
      return { success: false };
    }
    try {
      return await uiModelApiService.deleteCanvas(configureId, effectiveModelId, canvasId);
    } catch (error) {
      console.error('Failed to delete canvas:', error);
      return { success: false };
    }
  }, [config]);

  /**
   * Bulk delete canvases via API
   */
  const bulkDeleteCanvases = useCallback(async (canvasIds: string[], modelIdOverride?: string): Promise<{ success: boolean; deleted?: string[]; failed?: string[] }> => {
    const { configureId } = config;
    const effectiveModelId = modelIdOverride || config.modelId;
    if (!configureId || !effectiveModelId) {
      console.warn('bulkDeleteCanvases skipped: missing configureId or modelId');
      return { success: false, deleted: [], failed: canvasIds };
    }
    try {
      return await uiModelApiService.bulkDeleteCanvases(configureId, effectiveModelId, canvasIds);
    } catch (error) {
      console.error('Failed to bulk delete canvases:', error);
      return { success: false, deleted: [], failed: canvasIds };
    }
  }, [config]);

  /**
   * Update theme via API
   * Note: We accept Record<string, unknown> for flexibility but the API service
   * expects a specific UIModelTheme structure. Type assertion is used for compatibility.
   */
  const updateTheme = useCallback(async (
    projectId: string,
    modelId: string,
    theme: Record<string, unknown>
  ): Promise<boolean> => {
    try {
      // Type assertion needed - the API service expects UIModelTheme but we accept generic Record
      // for flexibility in the UI-Model-1 architecture
      await uiModelApiService.updateTheme(projectId, modelId, theme as any);
      return true;
    } catch (error) {
      console.error('Failed to update theme:', error);
      return false;
    }
  }, []);

  /**
   * Get custom CSS from API
   */
  const getCustomCss = useCallback(async (
    projectId: string,
    modelId: string
  ): Promise<{ done: boolean; data?: { globalStyles: string } }> => {
    try {
      const response = await getCustomCSS(projectId, modelId);
      return response;
    } catch (error) {
      console.error('Failed to get custom CSS:', error);
      return { done: false };
    }
  }, []);

  /**
   * Update custom CSS via API
   */
  const updateCustomCss = useCallback(async (
    projectId: string,
    modelId: string,
    css: { globalStyles: string }
  ): Promise<{ done: boolean }> => {
    try {
      const response = await updateCustomCSS(projectId, modelId, css);
      return response;
    } catch (error) {
      console.error('Failed to update custom CSS:', error);
      return { done: false };
    }
  }, []);

  const value = useMemo(
    () => ({
      config,
      isLoading,
      isSaving,
      lastError,
      updateConfig,
      loadModel,
      saveSection,
      saveAll,
      // Canvas deletion
      deleteCanvas,
      bulkDeleteCanvases,
      // Additional convenience methods for theme and CSS
      updateTheme,
      getCustomCss,
      updateCustomCss,
    }),
    [config, isLoading, isSaving, lastError, updateConfig, loadModel, saveSection, saveAll, deleteCanvas, bulkDeleteCanvases, updateTheme, getCustomCss, updateCustomCss]
  );

  return (
    <UIModelApiContext.Provider value={value}>
      {children}
    </UIModelApiContext.Provider>
  );
};

export const useUIModelApi = (): UIModelApiContextValue => {
  const context = useContext(UIModelApiContext);
  if (!context) {
    throw new Error('useUIModelApi must be used within a UIModelApiProvider');
  }
  return context;
};

export default UIModelApiContext;
